import { LinkContext } from ".";

import { AppError } from "config/errors";
import { GlobalErrorCallback, globalErrors } from "config/graphql/globalErrors";
import { ErrorCodes, IAppError } from "config/types";
import { Store } from "store";
import { logout } from "store/actions/authentication";
import { get as getApm } from "utils/elasticApm";
import { getAppError } from "utils/error";

import { CombinedGraphQLErrors, HttpLink, ServerError, ServerParseError } from "@apollo/client";
import { ErrorLink, onError } from "@apollo/client/link/error";
import { GraphQLFormattedError } from "graphql";
import { get } from "lodash-es";

export const errorHandler = (
  error: ErrorLink.ErrorHandlerOptions,
  context: {
    store: Store;
    errors: Partial<Record<ErrorCodes, GlobalErrorCallback>>;
  }
) => {
  const { store, errors } = context;
  const networkError =
    ServerError.is(error.error) || ServerParseError.is(error.error) ? error.error : undefined;

  const graphQlError = getAppError(error.error);
  const errorCode = get(graphQlError, "code", "") as ErrorCodes;
  const errorCallback = errors[errorCode];
  if (errorCode && errorCallback) {
    return errorCallback(store);
  } else if (networkError && (networkError as ServerError).statusCode === 401) {
    store.dispatch(logout());
  }
};

interface ICaptureErrors {
  graphQLErrors?: readonly GraphQLFormattedError[];
  networkError?: ServerError;
  store: Store;
}

/**
 * Reports errors to Elastic APM.
 *
 * Filtering is driven by AppError.shouldReport rather than a manually
 * maintained blacklist. Each error is classified by AppError's inference
 * logic and only reported when shouldReport is true. Adding a new error
 * code to the right subclass's codes set automatically suppresses it here.
 */
const captureErrors = ({ graphQLErrors = [], networkError, store }: ICaptureErrors) => {
  let elasticApm: ReturnType<typeof getApm>;
  try {
    elasticApm = getApm();
  } catch {
    // APM not yet initialized (e.g., local dev)
    return;
  }

  const {
    analyticsTracker: { utm: utmParams },
    user: { id: userId },
  } = store.getState();

  if (userId) {
    elasticApm.setUserContext({ id: userId });
  }

  if (networkError) {
    const appError = new AppError(networkError);
    if (appError.shouldReport) {
      // Wrapped in a try/catch to get the stack trace on Safari/IE
      try {
        throw networkError;
      } catch (err) {
        elasticApm.setCustomContext({
          path: null,
          utmParams: utmParams && JSON.stringify(utmParams),
        });
        elasticApm.captureError(err as Error);
      }
    }
  }

  graphQLErrors.forEach(graphQLError => {
    const appError = new AppError(graphQLError);
    if (!appError.shouldReport) {
      return;
    }
    // Wrapped in a try/catch to get the stack trace on Safari/IE
    try {
      throw graphQLError;
    } catch (err) {
      elasticApm.setCustomContext({
        path: graphQLError.path?.join("/"),
        utmParams: utmParams && JSON.stringify(utmParams),
      });
      elasticApm.captureError(err as Error);
    }
  });
};

const unauthorizedError = (networkError: ServerError | undefined, graphQlError: IAppError) => {
  const statusCode = networkError?.statusCode;
  const errorCode = graphQlError?.code || "";
  const graphQLUnauthorized = errorCode.toLowerCase() === "graph_api_unauthorized";
  return statusCode === 401 || graphQLUnauthorized;
};

export const errorLink = ({ store }: LinkContext) => {
  return onError(({ error }) => {
    const graphQLErrors = CombinedGraphQLErrors.is(error) ? error.errors : undefined;
    const networkError = ServerError.is(error) || ServerParseError.is(error) ? error : undefined;

    const graphQlError = getAppError(error);
    const errorCode = get(graphQlError, "code", "") as ErrorCodes;

    const globalErrorCallback = globalErrors[errorCode];
    const unauthorized = unauthorizedError(networkError, graphQlError);

    captureErrors({
      graphQLErrors,
      networkError,
      store,
    });

    if (errorCode && globalErrorCallback) {
      return globalErrorCallback(store);
    } else if (unauthorized) {
      store.dispatch(logout());
    }
  });
};

export const endpointLink = ({ endpoint }: LinkContext) =>
  new HttpLink({ uri: endpoint ? `${endpoint}/graphql` : "/graphql" });
